import { observer } from "mobx-react";
import * as React from "react";
import styled from "styled-components";

import Checkbox from "@client/components/ui/Checkbox";
import DropdownAction, {
  Action as DropdownActionType,
} from "@client/components/ui/DropdownAction";
import ReloadButton from "@client/components/ui/ReloadButton";
import SearchInput from "@client/components/ui/SearchInput";
import Style from "@client/utils/Style";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding-top: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;
const Main = styled.div`
  display: flex;
  margin-right: 16px;
  flex-grow: 1;
  margin-bottom: 32px;
  height: 32px;
  align-items: center;
`;
const FilterGroup = styled.div<{ noMargin?: boolean; selected?: boolean }>`
  display: flex;
  margin: 0 16px 0 ${(props: any) => (props.noMargin ? "0" : "32px")};
  border-right: 1px solid ${Style.palette.grayscale[4]};
`;
const FilterItem = styled.div<{ selected?: boolean }>`
  margin-right: 32px;
  color: ${props =>
    props.selected ? Style.palette.primary : Style.palette.grayscale[4]};
  ${props => (props.selected ? "text-decoration: underline;" : "")}
  cursor: pointer;
  white-space: nowrap;

  &:last-child {
    margin-right: 16px;
  }
`;
const Selection = styled.div`
  display: flex;
  align-items: center;
  transition: all ${Style.animations.swift};
  margin-bottom: 32px;
  animation: show-animation 0.4s;

  @keyframes show-animation {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;
const SelectionText = styled.div`
  margin-right: 16px;
  color: ${Style.palette.grayscale[4]};
  white-space: nowrap;
`;

type DictItem = { value: string; label: string };
type Props = {
  onFilterItemClick: (item: DictItem) => void;
  onReloadButtonClick: () => void;
  onSearchChange: (value: string) => void;
  onSelectAllChange: (checked: boolean) => void;
  dropdownActions: DropdownActionType[] | null;
  selectedValue: string;
  selectionInfo: { total: number; selected: number; label: string };
  selectAllSelected: boolean | null;
  items: DictItem[];
  customFilterComponent?: React.ReactNode;
  searchValue?: string;
};
@observer
class MainListFilter extends React.Component<Props> {
  renderFilterGroup() {
    const renderCustomComponent = () => {
      if (this.props.customFilterComponent) {
        return this.props.customFilterComponent;
      }
      return null;
    };

    return (
      <FilterGroup
        noMargin={
          !this.props.dropdownActions || this.props.dropdownActions.length === 0
        }
      >
        {renderCustomComponent()}
        {this.props.items.map(item => {
          return (
            <FilterItem
              onClick={() => this.props.onFilterItemClick(item)}
              key={item.value}
              selected={this.props.selectedValue === item.value}
            >
              {item.label}
            </FilterItem>
          );
        })}
      </FilterGroup>
    );
  }

  renderSelectionInfo() {
    if (!this.props.selectionInfo.selected) {
      return null;
    }

    return (
      <Selection>
        <SelectionText>
          {this.props.selectionInfo.selected} of{" "}
          {this.props.selectionInfo.total}
          &nbsp;
          {this.props.selectionInfo.label}
          (s) selected
        </SelectionText>
        {this.props.dropdownActions && this.props.dropdownActions.length ? (
          <DropdownAction
            actions={this.props.dropdownActions}
            style={{ marginLeft: "8px" }}
          />
        ) : null}
      </Selection>
    );
  }

  render() {
    const renderCheckbox = () => {
      if (this.props.dropdownActions && this.props.dropdownActions.length > 0) {
        return (
          <Checkbox
            onChange={checked => {
              this.props.onSelectAllChange(checked);
            }}
            checked={!!this.props.selectAllSelected}
          />
        );
      }
      return null;
    };

    return (
      <Wrapper>
        <Main>
          {renderCheckbox()}
          {this.renderFilterGroup()}
          <ReloadButton
            style={{ marginRight: "16px" }}
            onClick={this.props.onReloadButtonClick}
          />
          <SearchInput
            onChange={this.props.onSearchChange}
            value={this.props.searchValue}
          />
        </Main>
        {this.renderSelectionInfo()}
      </Wrapper>
    );
  }
}

export default MainListFilter;
